/**
 * Crew pickup screen (workstream C) — the crew's view of a warehouse run they're
 * on: Start / Finish, the ordered stops as a mapped journey, and the orders to
 * load with each packing list to check off ("received"). Built on the same crew
 * shell as CrewProjectDetail (BackBar + header), and reuses GET /pickups/:id
 * (crew-readable), so no crew-specific endpoint is needed.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  CheckCircle2,
  Circle,
  ClipboardList,
  Loader2,
  MapPin,
  Navigation,
  Play,
  RotateCcw,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { PickupProjectSummary, PickupStop } from '@vari-crew/schemas';
import { usePickup, useTransitionPickup } from '@/api/pickups';
import { useToggleLineItem } from '@/api/lineItems';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { journeyUrl, destinationUrl, type MapStop } from '@/lib/maps';
import { cn } from '@/lib/cn';
import { BackBar } from './CrewTaskDetail';

const PICKUP_UNDO_GRACE_MS = 2 * 60 * 1000;

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

function stopAddr(s: PickupStop): string {
  return [s.line1, [s.city, s.state].filter(Boolean).join(', '), s.postal_code].filter(Boolean).join(' · ');
}

export function CrewPickupDetail() {
  const { pickupId } = useParams<{ pickupId: string }>();
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const canAct = me?.role === 'crew' || me?.role === 'supervisor' || me?.role === 'admin';
  const { data: pickup, isLoading, error } = usePickup(pickupId);
  const transition = useTransitionPickup(pickupId ?? '');
  const goBack = () => navigate('/today');

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !pickup) {
    return (
      <div className="min-h-full bg-fill-soft">
        <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4">
          <BackBar onBack={goBack} />
          <div className="bg-bg rounded-2xl border border-line p-8 text-center text-sm text-ink-dim mt-4">
            {(error as Error | undefined)?.message ?? 'This pickup could not be found.'}
          </div>
        </div>
      </div>
    );
  }

  const running = pickup.status === 'in_progress';
  const finishedMs = pickup.finished_at ? parseISO(pickup.finished_at).getTime() : null;
  const canUndo = pickup.status === 'complete' && finishedMs != null && Date.now() - finishedMs < PICKUP_UNDO_GRACE_MS;

  function go(to: 'in_progress' | 'complete') {
    const said = to === 'in_progress' ? 'started' : 'finished';
    transition.mutate(
      { to },
      {
        onSuccess: () => toast.success(`Pickup ${said}`),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  }

  const journey = journeyUrl(pickup.stops as MapStop[]);

  return (
    <div className="min-h-full bg-fill-soft pb-10">
      <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackBar onBack={goBack} />

        <div>
          <div className="text-[11px] uppercase tracking-widest text-ink-faint">Pickup</div>
          <h1 className="text-xl font-semibold leading-tight mt-0.5">{pickup.display_title}</h1>
          <div className="text-sm text-ink-dim mt-0.5">
            {STATUS_LABEL[pickup.status] ?? pickup.status} · {pickup.projects.length}{' '}
            {pickup.projects.length === 1 ? 'order' : 'orders'}
          </div>
        </div>

        {/* Start / Finish */}
        <div
          className={cn(
            'rounded-2xl p-5 text-center',
            running ? 'bg-status-onsite text-status-onsite-fg' : 'bg-bg border border-line',
          )}
        >
          <div className="text-[11px] uppercase tracking-widest opacity-80">
            {running
              ? pickup.started_at
                ? `Started ${format(parseISO(pickup.started_at), 'h:mm a')}`
                : 'In progress'
              : pickup.status === 'complete'
                ? 'Picked up'
                : 'Ready to go'}
          </div>
          <div className="text-3xl font-bold tracking-tight mt-1">
            {pickup.status === 'complete' ? '✓' : running ? '▶' : '—'}
          </div>
          {canAct && pickup.status !== 'cancelled' && (
            <div className="flex gap-2 mt-4">
              {pickup.status === 'scheduled' && (
                <Button size="sm" className="flex-1 h-11" onClick={() => go('in_progress')} disabled={transition.isPending}>
                  <Play className="w-3.5 h-3.5" /> Start pickup
                </Button>
              )}
              {running && (
                <Button size="sm" className="flex-1 h-11" onClick={() => go('complete')} disabled={transition.isPending}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Finish
                </Button>
              )}
              {canUndo && (
                <Button size="sm" variant="outline" className="flex-1 h-11" onClick={() => go('in_progress')} disabled={transition.isPending}>
                  <RotateCcw className="w-3.5 h-3.5" /> Undo finish
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Journey / stops */}
        {pickup.stops.length > 0 && (
          <div className="bg-bg rounded-2xl border border-line p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-faint">
                <MapPin className="w-3.5 h-3.5" /> {pickup.stops.length} {pickup.stops.length === 1 ? 'stop' : 'stops'}
              </div>
              {journey && (
                <a href={journey} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-accent font-medium text-sm">
                  <Navigation className="w-3.5 h-3.5" /> Map route
                </a>
              )}
            </div>
            <ol className="space-y-2">
              {pickup.stops.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-fill text-ink-dim grid place-items-center text-[11px] font-medium">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.name}</div>
                    {stopAddr(s) && <div className="text-[11px] text-ink-faint truncate">{stopAddr(s)}</div>}
                  </div>
                  <a href={destinationUrl(s as MapStop)} target="_blank" rel="noreferrer" className="shrink-0 text-accent text-xs">
                    Map
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Orders + check-off */}
        <div className="bg-bg rounded-2xl border border-line p-4 space-y-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-faint">
            <ClipboardList className="w-3.5 h-3.5" /> Load these orders
          </div>
          {pickup.projects.length === 0 ? (
            <p className="text-sm text-ink-dim">No orders linked.</p>
          ) : (
            pickup.projects.map((p) => (
              <PickupOrder key={p.project_id} project={p} pickupId={pickup.id} canAct={canAct} />
            ))
          )}
        </div>

        {/* Who's with you */}
        {pickup.crew.length > 0 && (
          <div className="bg-bg rounded-2xl border border-line p-4">
            <div className="text-[11px] uppercase tracking-widest text-ink-faint mb-2">On this run</div>
            <ul className="space-y-1.5">
              {pickup.crew.map((c) => (
                <li key={c.crew_member_id} className="flex items-center gap-2 text-sm">
                  <Avatar name={c.display_name} src={c.avatar_url} className="w-7 h-7 text-[11px]" />
                  <span className="truncate">{c.display_name}</span>
                  <span className="text-ink-faint text-xs">· {c.crew_role}</span>
                  {c.is_lead && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 font-medium ml-auto">
                      <Star className="w-3 h-3 fill-amber-400" /> lead
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/** One order's packing list — the crew tap each line as they load it ("received"). */
function PickupOrder({
  project,
  pickupId,
  canAct,
}: {
  project: PickupProjectSummary;
  pickupId: string;
  canAct: boolean;
}) {
  const qc = useQueryClient();
  const toggle = useToggleLineItem(project.project_id);
  const [busyId, setBusyId] = useState<string | null>(null);

  function check(id: string, received: boolean) {
    if (!canAct) return;
    setBusyId(id);
    toggle.mutate(
      { id, received },
      {
        onSuccess: () => qc.invalidateQueries({ queryKey: ['pickups', 'detail', pickupId] }),
        onError: (e) => toast.error(`Could not update: ${e.message}`),
        onSettled: () => setBusyId(null),
      },
    );
  }

  return (
    <div className="rounded-lg border border-line p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{project.title}</div>
          {project.client_name && <div className="text-[11px] text-ink-faint truncate">{project.client_name}</div>}
        </div>
        {project.picked_up ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-status-complete-fg font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> Picked up
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-ink-faint">
            {project.packing.received}/{project.packing.total}
            {project.packing.backordered > 0 && ` · ${project.packing.backordered} B/O`}
          </span>
        )}
      </div>
      {project.line_items.length === 0 ? (
        <p className="text-[11px] text-ink-faint mt-1.5">No packing list on this order.</p>
      ) : (
        <ul className="mt-2 space-y-0.5">
          {project.line_items.map((it) => (
            <li key={it.id}>
              <button
                type="button"
                disabled={!canAct || busyId === it.id}
                onClick={() => check(it.id, !it.received)}
                className={cn(
                  'flex items-center gap-2 w-full text-left py-1.5 text-[13px] rounded',
                  canAct && 'hover:bg-fill-soft',
                )}
              >
                {busyId === it.id ? (
                  <Loader2 className="w-4 h-4 animate-spin text-ink-faint shrink-0" />
                ) : it.received ? (
                  <CheckCircle2 className="w-4 h-4 text-status-complete-fg shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-ink-faint shrink-0" />
                )}
                <span className={cn('min-w-0 flex-1 truncate', it.received && 'text-ink-faint line-through')}>
                  {it.product_code && <span className="text-ink-faint">{it.product_code} · </span>}
                  {it.description}
                </span>
                {it.is_backordered && (
                  <span className="shrink-0 text-[10px] text-amber-700">
                    B/O {it.shipped ?? 0}/{it.quantity ?? 0}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
